/**
 * Arena / pad grading: compare a designed pad surface with the existing terrain
 * and report earthwork volumes.
 *
 * Drainage options (all planes/surfaces pass through the pad's centroid at the
 * pad elevation, so "balanced" means cut equals fill inside the outline):
 *   level   - flat
 *   oneway  - single plane draining toward a compass bearing
 *   crown   - ridge down the long axis, falling to both long sides
 *   best    - one-way plane with the bearing/slope that moves the least dirt
 */
import { planToMap, mapToPlan } from './georef.js';

const CUBIC_FEET_PER_YARD = 27;

export const DRAINAGE_MODES = {
  level: 'Level',
  oneway: 'One-way slope',
  crown: 'Crowned',
  best: 'Least dirt moved',
};

export const DEFAULT_GRADING = {
  roomId: null,
  mode: 'best',
  slopePercent: 1,       // one-way slope
  bearing: 180,          // one-way: compass direction water drains toward
  crownPercent: 1,       // crowned: fall from ridge to each long side
  minSlopePercent: 0.5,  // least-dirt search range
  maxSlopePercent: 2,
  elevationMode: 'balance', // 'balance' cut and fill, or 'manual'
  manualElevation: null, // pad elevation at its center (ft)
  showOverlay: true,
};

export const pointInPolygon = (x, y, poly) => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x, yi = poly[i].y, xj = poly[j].x, yj = poly[j].y;
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
};

/**
 * Collect terrain cells inside a room outline (plan coordinates).
 * All distances/elevations are converted to feet.
 */
export const collectPadCells = (terrain, layer, outline) => {
  const feetPerUnit = layer.geo.unitsToPlanPx / 40;
  const poly = outline.map(p => planToMap(layer, p));
  const xs = poly.map(p => p.x), ys = poly.map(p => p.y);
  const { originX, originY, cellSize, cols, rows, data } = terrain;
  const c0 = Math.max(0, Math.floor((Math.min(...xs) - originX) / cellSize));
  const c1 = Math.min(cols - 1, Math.ceil((Math.max(...xs) - originX) / cellSize));
  const r0 = Math.max(0, Math.floor((originY - Math.max(...ys)) / cellSize));
  const r1 = Math.min(rows - 1, Math.ceil((originY - Math.min(...ys)) / cellSize));

  const cells = [];
  let missing = 0;
  let sx = 0, sy = 0;
  for (let r = r0; r <= r1; r++) {
    const y = originY - (r + 0.5) * cellSize;
    for (let c = c0; c <= c1; c++) {
      const x = originX + (c + 0.5) * cellSize;
      if (!pointInPolygon(x, y, poly)) continue;
      const z = data[r * cols + c];
      if (Number.isNaN(z)) { missing++; continue; }
      cells.push({ r, c, x: x * feetPerUnit, y: y * feetPerUnit, z: z * feetPerUnit });
      sx += x * feetPerUnit;
      sy += y * feetPerUnit;
    }
  }
  if (!cells.length) return null;

  const cx = sx / cells.length, cy = sy / cells.length;
  const n = cells.length;
  const dx = new Float64Array(n), dy = new Float64Array(n), z = new Float64Array(n);
  let zMin = Infinity, zMax = -Infinity;
  cells.forEach((cell, i) => {
    dx[i] = cell.x - cx;
    dy[i] = cell.y - cy;
    z[i] = cell.z;
    if (cell.z < zMin) zMin = cell.z;
    if (cell.z > zMax) zMax = cell.z;
  });

  // Long axis of the outline (its longest edge), in map orientation (east, north)
  let axis = { x: 1, y: 0 }, longest = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len > longest) { longest = len; axis = { x: (b.x - a.x) / len, y: (b.y - a.y) / len }; }
  }

  return {
    cells, dx, dy, z, zMin, zMax, cx, cy,
    rows: [r0, r1], cols: [c0, c1],
    cellArea: (cellSize * feetPerUnit) ** 2,
    missing,
    axis,
  };
};

// Design surface relative to the pad elevation, as a function of the offset
// (dx east, dy north, in ft) from the pad's center
const shapeFunction = (mode, params, axis) => {
  if (mode === 'oneway' || mode === 'best') {
    const s = params.slopePercent / 100;
    const rad = (params.bearing * Math.PI) / 180;
    const ex = Math.sin(rad), ny = Math.cos(rad);
    return (dx, dy) => -s * (dx * ex + dy * ny);
  }
  if (mode === 'crown') {
    const s = params.crownPercent / 100;
    const px = -axis.y, py = axis.x; // across the long axis
    return (dx, dy) => -s * Math.abs(dx * px + dy * py);
  }
  return () => 0;
};

const surfaceShape = (pad, mode, params) => {
  const { dx, dy } = pad;
  const f = shapeFunction(mode, params, pad.axis);
  const shape = new Float64Array(dx.length);
  for (let i = 0; i < dx.length; i++) shape[i] = f(dx[i], dy[i]);
  return shape;
};

const evaluate = (pad, shape, elevation) => {
  const { z, cellArea } = pad;
  const n = z.length;
  let padElevation = elevation;
  if (padElevation == null) {
    // Balanced: pad elevation where cut equals fill
    let sum = 0;
    for (let i = 0; i < n; i++) sum += z[i] - shape[i];
    padElevation = sum / n;
  }
  let cut = 0, fill = 0, maxCut = 0, maxFill = 0;
  for (let i = 0; i < n; i++) {
    const d = z[i] - (padElevation + shape[i]);
    if (d > 0) { cut += d; if (d > maxCut) maxCut = d; }
    else { fill -= d; if (-d > maxFill) maxFill = -d; }
  }
  return {
    padElevation,
    cutYards: (cut * cellArea) / CUBIC_FEET_PER_YARD,
    fillYards: (fill * cellArea) / CUBIC_FEET_PER_YARD,
    maxCut,
    maxFill,
  };
};

// Search bearings and slopes for the one-way plane that moves the least dirt
const findBestOneWay = (pad, settings) => {
  const lo = Math.max(0, settings.minSlopePercent);
  const hi = Math.max(lo, settings.maxSlopePercent);
  const score = (bearing, slopePercent) => {
    const shape = surfaceShape(pad, 'oneway', { bearing, slopePercent });
    const result = evaluate(pad, shape, null);
    return { bearing, slopePercent, total: result.cutYards + result.fillYards };
  };
  let best = null;
  const consider = (candidate) => { if (!best || candidate.total < best.total) best = candidate; };

  const slopeSteps = Math.max(1, Math.round((hi - lo) / 0.25));
  for (let b = 0; b < 360; b += 10) {
    for (let k = 0; k <= slopeSteps; k++) consider(score(b, lo + ((hi - lo) * k) / slopeSteps));
  }
  // Refine around the coarse winner
  const coarse = best;
  for (let b = coarse.bearing - 10; b <= coarse.bearing + 10; b += 2) {
    for (let s = coarse.slopePercent - 0.25; s <= coarse.slopePercent + 0.25 + 1e-9; s += 0.05) {
      if (s < lo - 1e-9 || s > hi + 1e-9) continue;
      consider(score((b + 360) % 360, s));
    }
  }
  return { bearing: Math.round(best.bearing), slopePercent: Math.round(best.slopePercent * 100) / 100 };
};

/**
 * Evaluate every drainage option for the pad. Returns
 * { pad stats, scenarios: { level, oneway, crown, best }, active, shape for active }
 */
export const gradePad = (pad, settings) => {
  const elevation = settings.elevationMode === 'manual' && Number.isFinite(settings.manualElevation)
    ? settings.manualElevation
    : null;
  const bestParams = findBestOneWay(pad, settings);

  const scenarios = {};
  const shapes = {};
  const params = {
    level: {},
    oneway: { bearing: settings.bearing, slopePercent: settings.slopePercent },
    crown: { crownPercent: settings.crownPercent },
    best: bestParams,
  };
  for (const mode of Object.keys(DRAINAGE_MODES)) {
    shapes[mode] = surfaceShape(pad, mode, params[mode]);
    scenarios[mode] = { ...evaluate(pad, shapes[mode], elevation), params: params[mode] };
  }

  const area = pad.cells.length * pad.cellArea;
  return {
    area,
    existingMin: pad.zMin,
    existingMax: pad.zMax,
    missingArea: pad.missing * pad.cellArea,
    scenarios,
    mode: settings.mode,
    activeShape: shapes[settings.mode],
    active: scenarios[settings.mode],
    center: { x: pad.cx, y: pad.cy },
    axis: pad.axis,
  };
};

/**
 * Design elevation (ft) of the active option at a map point given in feet
 */
export const designElevationAt = (grading, xFt, yFt) => {
  const { active, mode, center, axis } = grading;
  return active.padElevation + shapeFunction(mode, active.params, axis)(xFt - center.x, yFt - center.y);
};

/**
 * Cut/fill colour map for the active design, drawn in plan coordinates.
 * Red = cut (ground above design), blue = fill. Saturates at 2 ft.
 */
export const buildCutFillOverlay = (terrain, layer, pad, grading) => {
  const [r0, r1] = pad.rows, [c0, c1] = pad.cols;
  const width = c1 - c0 + 1, height = r1 - r0 + 1;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  const image = ctx.createImageData(width, height);
  const { padElevation } = grading.active;
  pad.cells.forEach((cell, i) => {
    const d = cell.z - (padElevation + grading.activeShape[i]);
    const t = Math.min(1, Math.abs(d) / 2);
    const o = ((cell.r - r0) * width + (cell.c - c0)) * 4;
    if (Math.abs(d) < 0.1) {
      image.data.set([80, 220, 120, 110], o); // on grade
    } else if (d > 0) {
      image.data.set([255, 200 - 170 * t, 60 - 40 * t, 120 + 110 * t], o);
    } else {
      image.data.set([60 - 40 * t, 160 - 60 * t, 255, 120 + 110 * t], o);
    }
  });
  ctx.putImageData(image, 0, 0);

  const { originX, originY, cellSize } = terrain;
  const topLeft = mapToPlan(layer, { x: originX + c0 * cellSize, y: originY - r0 * cellSize });
  const bottomRight = mapToPlan(layer, { x: originX + (c1 + 1) * cellSize, y: originY - (r1 + 1) * cellSize });
  return {
    canvas,
    x: topLeft.x,
    y: topLeft.y,
    width: bottomRight.x - topLeft.x,
    height: bottomRight.y - topLeft.y,
  };
};

export const bearingLabel = (bearing) => {
  const names = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return names[Math.round((((bearing % 360) + 360) % 360) / 45) % 8];
};

/**
 * Rectangle outline (plan coordinates) for an arena of the given size.
 * Width runs along `angle` (degrees, plan orientation), length perpendicular.
 */
export const arenaRectangle = (center, widthFt, lengthFt, angleDeg) => {
  const PX = 40;
  const a = (angleDeg * Math.PI) / 180;
  const ux = Math.cos(a), uy = Math.sin(a);
  const vx = -uy, vy = ux;
  const hw = (widthFt * PX) / 2, hl = (lengthFt * PX) / 2;
  return [
    { x: center.x - ux * hw - vx * hl, y: center.y - uy * hw - vy * hl },
    { x: center.x + ux * hw - vx * hl, y: center.y + uy * hw - vy * hl },
    { x: center.x + ux * hw + vx * hl, y: center.y + uy * hw + vy * hl },
    { x: center.x - ux * hw + vx * hl, y: center.y - uy * hw + vy * hl },
  ];
};

/**
 * Inverse of arenaRectangle for a 4-point room: { center, widthFt, lengthFt, angleDeg }
 */
export const describeRectangle = (points) => {
  if (!points || points.length !== 4) return null;
  const [p0, p1, p2, p3] = points;
  // Square corner at p1 and equal opposite sides, else it has been reshaped
  const ux = p1.x - p0.x, uy = p1.y - p0.y, vx = p2.x - p1.x, vy = p2.y - p1.y;
  const lu = Math.hypot(ux, uy), lv = Math.hypot(vx, vy);
  if (!lu || !lv || Math.abs((ux * vx + uy * vy) / (lu * lv)) > 0.01) return null;
  if (Math.abs(Math.hypot(p3.x - p2.x, p3.y - p2.y) - lu) > 1) return null;
  if (Math.abs(Math.hypot(p0.x - p3.x, p0.y - p3.y) - lv) > 1) return null;
  const center = {
    x: points.reduce((s, p) => s + p.x, 0) / 4,
    y: points.reduce((s, p) => s + p.y, 0) / 4,
  };
  return {
    center,
    widthFt: Math.hypot(p1.x - p0.x, p1.y - p0.y) / 40,
    lengthFt: Math.hypot(p2.x - p1.x, p2.y - p1.y) / 40,
    angleDeg: (Math.atan2(p1.y - p0.y, p1.x - p0.x) * 180) / Math.PI,
  };
};

/**
 * Arena outline: a rectangle whose corners may be rounded, as panel-built
 * arenas usually are. Each quarter-circle corner is made of straight chords
 * about `chordFt` long so a panel can span each one.
 *   { center, widthFt, lengthFt, angleDeg, cornerRadiusFt = 0, chordFt = 12 }
 * Edge 0 is always the straight part of the first width side, so the sides
 * keep their numbers whatever the corner radius.
 */
export const arenaOutline = ({ center, widthFt, lengthFt, angleDeg, cornerRadiusFt = 0, chordFt = 12 }) => {
  const r = Math.max(0, Math.min(cornerRadiusFt, widthFt / 2 - 0.5, lengthFt / 2 - 0.5));
  if (r < 0.5) return arenaRectangle(center, widthFt, lengthFt, angleDeg);
  const PX = 40;
  const a = (angleDeg * Math.PI) / 180;
  const ux = Math.cos(a), uy = Math.sin(a);
  const toPlan = (u, v) => ({
    x: center.x + (ux * u - uy * v) * PX,
    y: center.y + (uy * u + ux * v) * PX,
  });
  const hw = widthFt / 2, hl = lengthFt / 2;
  const steps = Math.max(1, Math.round((Math.PI / 2) * r / chordFt));
  // Corners in order with the angle their arc starts at (local frame, v = length)
  const corners = [
    { cu: hw - r, cv: -hl + r, start: -Math.PI / 2 },
    { cu: hw - r, cv: hl - r, start: 0 },
    { cu: -hw + r, cv: hl - r, start: Math.PI / 2 },
    { cu: -hw + r, cv: -hl + r, start: Math.PI },
  ];
  const points = [toPlan(-hw + r, -hl)];
  corners.forEach(({ cu, cv, start }, k) => {
    for (let s = 0; s <= steps; s++) {
      if (k === 3 && s === steps) break; // closes back on the first point
      const t = start + (Math.PI / 2) * (s / steps);
      points.push(toPlan(cu + r * Math.cos(t), cv + r * Math.sin(t)));
    }
  });
  return points;
};

const centroidOf = (points) => ({
  x: points.reduce((s, p) => s + p.x, 0) / points.length,
  y: points.reduce((s, p) => s + p.y, 0) / points.length,
});

/**
 * Panel-built corners: panels can't bend, so each corner chord is one panel
 * long and the radius follows from that. Keeps about the requested radius.
 */
export const fitCornersToPanels = (shape, panelLengthFt) => {
  const r = shape.cornerRadiusFt || 0;
  if (r < 0.5) return { ...shape, chordFt: panelLengthFt };
  const steps = Math.max(1, Math.round((Math.PI / 2) * r / panelLengthFt));
  const radius = panelLengthFt / (2 * Math.sin(Math.PI / 4 / steps));
  return { ...shape, cornerRadiusFt: Math.round(radius * 100) / 100, chordFt: panelLengthFt };
};

/**
 * Shape settings of an arena outline, or null for a free-form outline.
 * Outlines made by arenaOutline carry `room.arena`; they still count as that
 * shape after being moved or turned, but not once a corner is dragged.
 */
export const describeArena = (room) => {
  const points = room?.points;
  if (!points?.length) return null;
  const meta = room.arena;
  if (meta) {
    const [p0, p1] = points;
    const shape = {
      ...meta,
      center: centroidOf(points),
      angleDeg: (Math.atan2(p1.y - p0.y, p1.x - p0.x) * 180) / Math.PI,
    };
    const regenerated = arenaOutline(shape);
    if (regenerated.length === points.length
      && regenerated.every((p, i) => Math.hypot(p.x - points[i].x, p.y - points[i].y) < 2)) {
      return shape;
    }
  }
  const rectangle = describeRectangle(points);
  return rectangle ? { ...rectangle, cornerRadiusFt: 0, chordFt: 12, alley: meta?.alley || null } : null;
};

/**
 * Room fields for an arena outline built from shape settings
 */
export const arenaRoomFields = (shape) => ({
  points: arenaOutline(shape),
  arena: {
    widthFt: shape.widthFt,
    lengthFt: shape.lengthFt,
    cornerRadiusFt: shape.cornerRadiusFt || 0,
    chordFt: shape.chordFt || 12,
    alley: shape.alley || null,
    presetId: shape.presetId || null,
  },
});

/**
 * Return alley: a lane `alley.widthFt` wide outside one long side of the
 * arena, along its straight part. Returns the fence run (open polyline, plan
 * points) from one end cap to the other, or null.
 *   alley: { side: 'right' | 'left', widthFt }
 */
export const arenaAlleyRun = (shape) => {
  const alley = shape?.alley;
  if (!alley?.widthFt) return null;
  const PX = 40;
  const a = (shape.angleDeg * Math.PI) / 180;
  const ux = Math.cos(a), uy = Math.sin(a);
  const toPlan = (u, v) => ({
    x: shape.center.x + (ux * u - uy * v) * PX,
    y: shape.center.y + (uy * u + ux * v) * PX,
  });
  const hw = shape.widthFt / 2, hl = shape.lengthFt / 2;
  const r = Math.max(0, Math.min(shape.cornerRadiusFt || 0, hw - 0.5, hl - 0.5));
  const sign = alley.side === 'left' ? -1 : 1;
  const inner = sign * hw, outer = sign * (hw + alley.widthFt);
  const v0 = -hl + r, v1 = hl - r;
  return [toPlan(inner, v0), toPlan(outer, v0), toPlan(outer, v1), toPlan(inner, v1)];
};

/**
 * Arena sizes from Priefert's published roping-arena layouts, plus common
 * riding sizes. Priefert doesn't publish its corner radius; their layout
 * drawings use two angled 12' panels per corner, about a 16 ft radius.
 */
export const ARENA_PRESETS = [
  { id: 'priefert-120x240', label: "Priefert basic roping 120' × 240'", widthFt: 120, lengthFt: 240, cornerRadiusFt: 16 },
  { id: 'priefert-150x250', label: "Priefert roping 150' × 250'", widthFt: 150, lengthFt: 250, cornerRadiusFt: 16 },
  { id: 'priefert-150x250-6', label: "Priefert 150' × 250' + 6' return alley", widthFt: 150, lengthFt: 250, cornerRadiusFt: 16, alley: { side: 'right', widthFt: 6 } },
  { id: 'priefert-150x250-10', label: "Priefert 150' × 250' + 10' return alley", widthFt: 150, lengthFt: 250, cornerRadiusFt: 16, alley: { side: 'right', widthFt: 10 } },
  { id: 'priefert-140x270', label: "Priefert combination team & calf 140' × 270'", widthFt: 140, lengthFt: 270, cornerRadiusFt: 16, alley: { side: 'right', widthFt: 10 } },
  { id: 'priefert-160x275', label: "Priefert side-by-side: team arena 160' × 275'", widthFt: 160, lengthFt: 275, cornerRadiusFt: 16, alley: { side: 'right', widthFt: 10 } },
  { id: 'priefert-50x275', label: "Priefert side-by-side: calf arena 50' × 275'", widthFt: 50, lengthFt: 275, cornerRadiusFt: 15 },
  // Red River Arenas drawings: return alley down the left side; the Eliminator
  // turns each corner with one angled 12' panel, the Team Roper with two
  {
    id: 'redriver-eliminator-100x200', label: "Red River Eliminator 100' × 200' w/ return", widthFt: 100, lengthFt: 200, cornerRadiusFt: 8.5,
    alley: { side: 'left', widthFt: 10 },
    reference: {
      source: 'Red River Arenas drawing TUBE-S12-29',
      parts: [["12' panels", 60], ["12' DC panels", 4], ["10' panels", 3], ["10' DC panels", 3], ["16' panels", 1], ["16' DC panels", 1],
        ["6' gates", 2], ["10' gates", 2], ["12' gates", 1], ['Roping chute', 1], ['Stripping chute', 1], ['LSection 90', 1]],
    },
  },
  {
    id: 'redriver-team-roper-140x240', label: "Red River Team Roper 140' × 240' w/ return", widthFt: 140, lengthFt: 240, cornerRadiusFt: 16,
    alley: { side: 'left', widthFt: 10 },
    reference: {
      source: 'Red River Arenas drawing TUBE-S12-00-A',
      parts: [["12' panels", 74], ["12' DC panels", 4], ["10' panels", 5], ["10' DC panels", 2],
        ["6' gates", 2], ["10' gates", 2], ["12' gates", 1], ['Roping chute', 1], ['Stripping chute', 1], ['LSection 90', 1]],
    },
  },
  { id: 'riding-100x200', label: "Riding arena 100' × 200'", widthFt: 100, lengthFt: 200, cornerRadiusFt: 0 },
  { id: 'dressage-20x40', label: 'Dressage 20 × 40 m', widthFt: 66, lengthFt: 131, cornerRadiusFt: 0 },
  { id: 'dressage-20x60', label: 'Dressage 20 × 60 m', widthFt: 66, lengthFt: 197, cornerRadiusFt: 0 },
];

/**
 * Best-fitting rectangle around any outline (smallest-area, aligned to one of
 * its edges), as arena shape settings: the long sides run along the length.
 * Lets equipment be laid out on outlines that were reshaped by hand.
 */
export const approximateArenaFrame = (points) => {
  if (!points || points.length < 3) return null;
  let best = null;
  points.forEach((a, i) => {
    const b = points[(i + 1) % points.length];
    const angle = Math.atan2(b.y - a.y, b.x - a.x);
    const c = Math.cos(angle), s = Math.sin(angle);
    let minU = Infinity, maxU = -Infinity, minV = Infinity, maxV = -Infinity;
    points.forEach(p => {
      const u = p.x * c + p.y * s, v = -p.x * s + p.y * c;
      minU = Math.min(minU, u); maxU = Math.max(maxU, u);
      minV = Math.min(minV, v); maxV = Math.max(maxV, v);
    });
    const area = (maxU - minU) * (maxV - minV);
    if (!best || area < best.area) best = { area, angle, minU, maxU, minV, maxV };
  });
  if (!best || best.area <= 0) return null;
  const { angle, minU, maxU, minV, maxV } = best;
  const cu = (minU + maxU) / 2, cv = (minV + maxV) / 2;
  const c = Math.cos(angle), s = Math.sin(angle);
  const center = { x: cu * c - cv * s, y: cu * s + cv * c };
  let widthFt = (maxU - minU) / 40, lengthFt = (maxV - minV) / 40;
  let angleDeg = (angle * 180) / Math.PI;
  // Width runs along the arena's ends, so it is the shorter dimension
  if (widthFt > lengthFt) {
    [widthFt, lengthFt] = [lengthFt, widthFt];
    angleDeg += 90;
  }
  return { center, widthFt, lengthFt, angleDeg, cornerRadiusFt: 0, alley: null };
};
